import { useEffect, useState } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Runs a screen's load, and says whether the FIRST one is still in flight.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Takes the place of `useEffect(() => { void reload(); }, [reload])` — it runs `load` whenever it
 * changes, exactly as that did — and returns `true` until the first run settles, success or
 * failure. A list passes it as `isLoading` and draws skeleton rows instead of an empty state that
 * would tell somebody to add the first one while theirs are arriving (`state-rules.md` §1, as the
 * product owner amended it on 13 Oct 2026).
 *
 * ⚠️⚠️ **THE FIRST RUN ONLY.** A changed filter makes a new `load` and runs it again, and a live
 * invalidation calls it directly; both re-read behind rows that are already on screen, and
 * blinking those back to a skeleton would be the screen claiming to know less than it does.
 *
 * ⚠️ **Settled on failure too.** `ScreenState` replaces the screen with its error, and a skeleton
 * left running underneath would come back the moment somebody pressed retry.
 */
export function useFirstLoad(load: () => Promise<unknown>): boolean {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void load().finally(() => {
      setLoading(false);
    });
  }, [load]);

  return loading;
}
